import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { handleApiError, HttpError } from '@/lib/errors';
import { type Product, type ProductVariant } from '@/lib/shop';
import { trackAddToCart, trackBeginCheckout } from '@/lib/analytics';

export interface CartItem {
  productId: string;
  productTitle: string;
  productSlug: string;
  variantId: string;
  variantTitle: string;
  variantSku: string | null;
  priceOre: number;
  taxRateBp: number;
  imageUrl: string | null;
  quantity: number;
}

interface CartStore {
  items: CartItem[];
  isCheckingOut: boolean;
  addItem: (product: Product, variant: ProductVariant, quantity?: number) => void;
  updateQuantity: (variantId: string, quantity: number) => void;
  removeItem: (variantId: string) => void;
  clearCart: () => void;
  /** Start a Kustom checkout session and redirect the browser to Kustom. */
  checkout: () => Promise<void>;
  totalItems: () => number;
  totalOre: () => number;
  totalTaxOre: () => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      isCheckingOut: false,

      addItem: (product, variant, quantity = 1) => {
        trackAddToCart({
          productSlug: product.slug,
          variantId: variant.id,
          priceOre: variant.priceOre,
          quantity,
        });
        const items = get().items;
        const existing = items.find((i) => i.variantId === variant.id);
        if (existing) {
          set({
            items: items.map((i) =>
              i.variantId === variant.id
                ? { ...i, quantity: i.quantity + quantity }
                : i
            ),
          });
          return;
        }
        const primaryImage = product.images[0]?.url ?? null;
        set({
          items: [
            ...items,
            {
              productId: product.id,
              productTitle: product.title,
              productSlug: product.slug,
              variantId: variant.id,
              variantTitle: variant.title,
              variantSku: variant.sku,
              priceOre: variant.priceOre,
              taxRateBp: variant.taxRateBp,
              imageUrl: primaryImage,
              quantity,
            },
          ],
        });
      },

      updateQuantity: (variantId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(variantId);
          return;
        }
        set({
          items: get().items.map((i) =>
            i.variantId === variantId ? { ...i, quantity } : i
          ),
        });
      },

      removeItem: (variantId) => {
        set({
          items: get().items.filter((i) => i.variantId !== variantId),
        });
      },

      clearCart: () => set({ items: [] }),

      checkout: async () => {
        const { items, isCheckingOut } = get();
        if (isCheckingOut || items.length === 0) return;
        trackBeginCheckout({
          itemCount: get().totalItems(),
          totalOre: get().totalOre(),
        });
        set({ isCheckingOut: true });
        try {
          const res = await fetch('/api/kustom-checkout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: items.map((i) => ({
                variantId: i.variantId,
                quantity: i.quantity,
              })),
            }),
          });
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new HttpError(res.status, data?.error ?? 'Checkout feilet');
          }
          const data = await res.json();
          if (!data.redirect_url) {
            throw new Error('Mottok ikke checkout-lenke fra Kustom');
          }
          window.location.href = data.redirect_url;
        } catch (error) {
          handleApiError(error, {
            source: 'cartStore.checkout',
            fallbackMessage: 'Kunne ikke starte checkout. Prøv igjen.',
            context: { itemCount: items.length },
          });
          set({ isCheckingOut: false });
        }
      },

      totalItems: () => get().items.reduce((sum, i) => sum + i.quantity, 0),
      totalOre: () =>
        get().items.reduce((sum, i) => sum + i.priceOre * i.quantity, 0),
      totalTaxOre: () =>
        get().items.reduce((sum, i) => {
          const gross = i.priceOre * i.quantity;
          // tax already included in gross price; tax portion = gross * rate / (10000 + rate)
          return sum + Math.round((gross * i.taxRateBp) / (10000 + i.taxRateBp));
        }, 0),
    }),
    {
      name: 'hapnaturlab-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ items: state.items }),
    }
  )
);
